import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  ButtonSpinner,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Input,
  NativeSelect,
  SearchInput,
  Skeleton,
  toast,
} from '@eureca/ui';
import { CheckIcon, RefreshCwIcon, TriangleAlertIcon, XIcon } from './icons';
import {
  fatorHSyncCandidatesQueryOptions,
  fatorHSyncQueryKey,
  friendlyPushError,
  pushFatorHCandidates,
  type FatorHIntegrationState,
  type FatorHSyncCandidate,
} from './fator-h-sync-query';

interface FatorHSyncSheetProps {
  /** Vaga cujos candidatos serão enviados/consultados no FatorH (= jobId). */
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Tab = 'enviar' | 'resultados';
type SendFilter = 'todos' | 'pendentes' | 'enviados';

/**
 * Painel FatorH da vaga (aberto pelo ícone no card do módulo FatorH). Duas abas
 * com jobs distintos:
 *   - **Enviar**: garante que os candidatos estejam no FatorH pra fazer a
 *     avaliação. Foca em quem falta; enviar é idempotente (reenviar é seguro).
 *   - **Resultados**: notas de quem já fez a avaliação, pra consultar/filtrar.
 *
 * A nota NÃO filtra o envio (nota só existe depois de responder) — por isso vive
 * na aba de Resultados, não na de Enviar.
 */
export function FatorHSyncSheet({ jobId, open, onOpenChange }: FatorHSyncSheetProps) {
  const queryClient = useQueryClient();
  const query = useQuery(fatorHSyncCandidatesQueryOptions(jobId, open));
  const candidates = useMemo(() => query.data?.candidates ?? [], [query.data]);

  const [tab, setTab] = useState<Tab>('enviar');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim().toLowerCase(), 250);
  const [sendFilter, setSendFilter] = useState<SendFilter>('pendentes');
  const [minScore, setMinScore] = useState('');

  // Push state. `pushingUserId` = linha em push individual (null durante o envio
  // em massa). `failedById` = quem falhou no último push → MOTIVO (spec 068:
  // "Falhou" sem o porquê deixava o operador cego; o core já devolve o erro).
  const [pushingUserId, setPushingUserId] = useState<string | null>(null);
  const [failedById, setFailedById] = useState<Map<string, string>>(() => new Map());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const pending = useMemo(() => candidates.filter((c) => !c.pushed), [candidates]);
  const answeredCount = useMemo(
    () => candidates.filter((c) => c.answeredAt !== null).length,
    [candidates]
  );

  const pushMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      pushFatorHCandidates(jobId, userIds, (done, total) => setProgress({ done, total })),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: fatorHSyncQueryKey(jobId) });
      setFailedById(new Map(result.failed.map((f) => [f.userId, friendlyPushError(f.error)])));
      const ok = result.pushed.length;
      const failed = result.failed.length;
      const skipped = result.skipped.length;
      if (failed > 0) {
        toast.error(
          `${ok} enviado(s), ${failed} falharam${skipped ? `, ${skipped} sem alteração` : ''}. Você pode reenviar os que falharam.`
        );
      } else {
        toast.success(
          `${ok} candidato(s) enviado(s)${skipped ? `, ${skipped} sem alteração` : ''}.`
        );
      }
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Falha ao enviar para o FatorH'),
    onSettled: () => {
      setPushingUserId(null);
      setProgress(null);
    },
  });

  const bulkPushing = pushMutation.isPending && pushingUserId === null;

  function handlePushPending() {
    const ids = pending.map((c) => c.userId);
    if (ids.length === 0) return;
    setPushingUserId(null);
    pushMutation.mutate(ids);
  }

  function handlePushOne(userId: string) {
    setPushingUserId(userId);
    setFailedById((prev) => {
      const next = new Map(prev);
      next.delete(userId);
      return next;
    });
    pushMutation.mutate([userId]);
  }

  const sendList = useMemo(
    () =>
      candidates
        .filter((c) => matchesSearch(c, debouncedSearch))
        .filter((c) =>
          sendFilter === 'todos' ? true : sendFilter === 'pendentes' ? !c.pushed : c.pushed
        ),
    [candidates, debouncedSearch, sendFilter]
  );

  const resultsList = useMemo(() => {
    const min = minScore.trim() === '' ? null : Number(minScore);
    return candidates
      .filter((c) => c.answeredAt !== null)
      .filter((c) => matchesSearch(c, debouncedSearch))
      .filter((c) => min === null || Number.isNaN(min) || (c.score !== null && c.score >= min))
      .sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  }, [candidates, debouncedSearch, minScore]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className={cn(
          'gap-0 p-0',
          'lg:left-auto lg:right-0 lg:top-0 lg:bottom-0',
          'lg:h-dvh lg:max-h-dvh lg:w-full lg:max-w-[560px]',
          'lg:translate-x-0 lg:translate-y-0 lg:rounded-none',
          'flex flex-col',
          'data-[state=open]:animate-sheet-in-bottom data-[state=closed]:animate-sheet-out-bottom',
          'lg:data-[state=open]:animate-sheet-in-right lg:data-[state=closed]:animate-sheet-out-right'
        )}
      >
        <header className="flex flex-row items-center gap-1.5 px-6 pt-6 pb-3">
          <DialogTitle className="flex-1 text-lg font-semibold leading-none tracking-tight text-foreground">
            FatorH
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Fechar"
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="px-6 pb-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome ou e-mail"
            />
          </div>

          <IntegrationBanner integration={query.data?.integration ?? null} />

          <div className="flex flex-row gap-6 border-b border-border px-6">
            <TabButton active={tab === 'enviar'} onClick={() => setTab('enviar')}>
              Enviar{pending.length > 0 ? ` (${pending.length})` : ''}
            </TabButton>
            <TabButton active={tab === 'resultados'} onClick={() => setTab('resultados')}>
              Resultados{answeredCount > 0 ? ` (${answeredCount})` : ''}
            </TabButton>
          </div>

          {tab === 'enviar' ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex flex-col gap-3 px-6 pt-4 pb-3">
                <p className="text-sm leading-5 text-muted-foreground">
                  Cada candidato da vaga precisa estar no FatorH para fazer a avaliação. Envie quem
                  ainda falta — incluindo quem se inscreveu antes de você adicionar o FatorH. Pode
                  reenviar sem medo: não duplica nem apaga respostas.
                </p>

                {pending.length > 0 ? (
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      onClick={handlePushPending}
                      disabled={pushMutation.isPending}
                    >
                      {bulkPushing ? <ButtonSpinner /> : <RefreshCwIcon className="size-4" />}
                      Enviar {pending.length} pendente{pending.length === 1 ? '' : 's'}
                    </Button>
                    {bulkPushing && progress ? (
                      <span className="text-xs text-muted-foreground">
                        enviando {progress.done}/{progress.total}…
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-1.5 rounded-lg bg-muted px-2 py-1 text-xs font-medium leading-4 text-muted-foreground">
                    <CheckIcon className="size-3.5 shrink-0" />
                    Todos os candidatos já estão no FatorH
                  </div>
                )}

                <NativeSelect
                  aria-label="Filtrar por estado de envio"
                  value={sendFilter}
                  onChange={(e) => setSendFilter(e.target.value as SendFilter)}
                  className="w-48"
                >
                  <option value="pendentes">Pendentes</option>
                  <option value="enviados">Já enviados</option>
                  <option value="todos">Todos</option>
                </NativeSelect>
              </div>

              <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pb-4">
                {query.isLoading ? (
                  <ListSkeleton />
                ) : query.isError ? (
                  <ErrorText />
                ) : sendList.length === 0 ? (
                  <EmptyText total={candidates.length} />
                ) : (
                  sendList.map((c) => (
                    <SendRow
                      key={c.userId}
                      candidate={c}
                      failureReason={failedById.get(c.userId) ?? null}
                      pushing={pushMutation.isPending && pushingUserId === c.userId}
                      disabled={pushMutation.isPending}
                      onPush={() => handlePushOne(c.userId)}
                    />
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex flex-col gap-3 px-6 pt-4 pb-3">
                <p className="text-sm leading-5 text-muted-foreground">
                  Notas de quem já fez a avaliação FatorH nesta vaga.
                </p>
                <Input
                  type="number"
                  inputMode="numeric"
                  aria-label="Nota mínima"
                  placeholder="Nota mínima"
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                  className="w-36"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pb-4">
                {query.isLoading ? (
                  <ListSkeleton />
                ) : query.isError ? (
                  <ErrorText />
                ) : resultsList.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {answeredCount === 0
                      ? 'Ninguém fez a avaliação ainda.'
                      : 'Nenhum resultado com os filtros atuais.'}
                  </p>
                ) : (
                  resultsList.map((c) => <ResultRow key={c.userId} candidate={c} />)
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Banner de remediação (spec 068): integração quebrada nunca mais falha em
 * silêncio — cada estado aponta o passo concreto. `null`/estado saudável = nada.
 */
function IntegrationBanner({ integration }: { integration: FatorHIntegrationState | null }) {
  if (!integration) return null;
  const { workspaceBound, hasKey, examMapped } = integration;
  let message: string | null = null;
  if (!workspaceBound || !hasKey) {
    message =
      'Integração FatorH incompleta para esta empresa — configure a Chave de API e o Workspace em Empresas → Editar → Integrações. Os envios não funcionam até isso.';
  } else if (!examMapped) {
    message =
      'O módulo FatorH desta vaga está sem teste vinculado — abra Editar no módulo, selecione o teste e salve o fluxo.';
  }
  if (!message) return null;
  return (
    <div
      role="alert"
      className="mx-6 mb-3 flex flex-row items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs leading-4 text-destructive"
    >
      <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-1 py-2 text-sm font-medium transition-colors focus-visible:outline-none',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

function SendRow({
  candidate,
  failureReason,
  pushing,
  disabled,
  onPush,
}: {
  candidate: FatorHSyncCandidate;
  /** Motivo do último push falho desta linha (null = não falhou). */
  failureReason: string | null;
  pushing: boolean;
  disabled: boolean;
  onPush: () => void;
}) {
  const { name, email, pushed, answeredAt } = candidate;
  const failed = failureReason !== null;
  const primary = name ?? email ?? '—';
  return (
    <div className="flex flex-row items-center gap-3 rounded-xl border border-border bg-card p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm font-medium leading-none text-foreground">{primary}</p>
        {name && email ? (
          <p className="truncate text-xs leading-none text-muted-foreground">{email}</p>
        ) : null}
        <div className="flex flex-row flex-wrap items-center gap-1.5 pt-0.5">
          {pushed ? (
            <Badge variant="secondary" className="gap-1">
              <CheckIcon className="size-3" />
              No FatorH
            </Badge>
          ) : failed ? (
            <Badge variant="destructive" className="gap-1">
              <TriangleAlertIcon className="size-3" />
              Falhou
            </Badge>
          ) : (
            <Badge variant="outline">Pendente</Badge>
          )}
          {answeredAt ? <Badge variant="outline">Respondeu</Badge> : null}
        </div>
        {failureReason ? (
          <p className="text-xs leading-4 text-destructive" title={failureReason}>
            {failureReason}
          </p>
        ) : null}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onPush} disabled={disabled}>
        {pushing ? <ButtonSpinner /> : <RefreshCwIcon className="size-4" />}
        {pushed || failed ? 'Reenviar' : 'Enviar'}
      </Button>
    </div>
  );
}

function ResultRow({ candidate }: { candidate: FatorHSyncCandidate }) {
  const { name, email, score, answeredAt } = candidate;
  const primary = name ?? email ?? '—';
  return (
    <div className="flex flex-row items-center gap-3 rounded-xl border border-border bg-card p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm font-medium leading-none text-foreground">{primary}</p>
        {name && email ? (
          <p className="truncate text-xs leading-none text-muted-foreground">{email}</p>
        ) : null}
        {answeredAt ? (
          <p className="text-xs leading-none text-muted-foreground">
            Respondeu em {formatDate(answeredAt)}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end">
        <span className="text-lg font-semibold leading-none text-foreground">
          {score === null ? '—' : score}
        </span>
        <span className="text-[11px] uppercase leading-none text-muted-foreground">nota</span>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 w-full rounded-xl" />
      ))}
    </>
  );
}

function ErrorText() {
  return (
    <p className="text-sm text-destructive">
      Falha ao carregar os candidatos. Feche e reabra o painel.
    </p>
  );
}

function EmptyText({ total }: { total: number }) {
  return (
    <p className="text-sm text-muted-foreground">
      {total === 0
        ? 'Nenhum candidato ativo nesta vaga.'
        : 'Nenhum candidato com os filtros atuais.'}
    </p>
  );
}

function matchesSearch(c: FatorHSyncCandidate, query: string): boolean {
  if (!query) return true;
  return `${c.name ?? ''} ${c.email ?? ''}`.toLowerCase().includes(query);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

/** Valor atrasado por `delayMs` — usado pra debouncar a busca por nome. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
